import { PERMISSIONS } from "@/services/permission"
import { FEATURE_SETS } from "@/services/featureSet"
import { isEntitled } from "@/services/entitlements"

// Either kind's purchase prices; the server leaves out a kind without them (and its records' right).
const INSIGHT_PRICE_PERMISSIONS = [PERMISSIONS.VIEW_MATERIAL_PURCHASEPRICE, PERMISSIONS.VIEW_MACHINE_PURCHASEPRICE]

// After `featureSetGuard` has let SUPPLIER_INSIGHTS through: the records it analyses are a set of their own. Also the
// guard of the materials' and machines' Vergleich.
export const requireSuppliers = () =>
  isEntitled(FEATURE_SETS.SUPPLIERS) || { name: "FeatureLocked", params: { featureSet: FEATURE_SETS.SUPPLIERS } }

// Lieferanten. The backend gates `/supplier/**` on the SUPPLIERS feature-set (403 FEATURE_NOT_ENTITLED),
// which every plan holds once released; until then `featureSetGuard` sends a workspace without it to the
// "not available yet" page, as for the other unreleased sets.
export default [
  {
    path: "/supplier/list",
    name: "SupplierOverview",
    component: () => import("@/views/supplier/SupplierOverview.vue"),
    meta: {
      title: "nav.supplier.overview",
      requiresAuth: true,
      requiresAllPermissions: [PERMISSIONS.VIEW_SUPPLIER_GENERAL],
      requiresFeatureSet: FEATURE_SETS.SUPPLIERS,
    },
  },
  // The overview's analysis (docs/suppliers.md, "Auswertungen"): the paid add-on SUPPLIER_INSIGHTS, whose
  // "Freischalten" page a workspace without it lands on. It reads every supplier and the prices of either kind
  // (the view offers the kinds whose records and prices the reader may see). The add-on sits on top of
  // SUPPLIERS, which the guard cannot ask in the same breath; a workspace without the setup gets the view's
  // own note.
  {
    path: "/supplier/comparison",
    name: "SupplierComparison",
    component: () => import("@/views/supplier/SupplierComparison.vue"),
    beforeEnter: requireSuppliers,
    meta: {
      title: "supplierInsights.comparison.title",
      requiresAuth: true,
      requiresAllPermissions: [PERMISSIONS.VIEW_SUPPLIER_GENERAL],
      requiresPermissions: INSIGHT_PRICE_PERMISSIONS,
      requiresFeatureSet: FEATURE_SETS.SUPPLIER_INSIGHTS,
    },
  },
  {
    // Before the record route only for reading's sake: a static segment outranks `:supplierId` anyway.
    path: "/supplier/setup",
    name: "SupplierSetup",
    component: () => import("@/views/supplier/SupplierSetup.vue"),
    meta: {
      title: "supplier.setup.title",
      requiresAuth: true,
      // What the server asks: it creates suppliers and rewrites every machine's and material's prices.
      requiresAllPermissions: [
        PERMISSIONS.EDIT_SUPPLIER_GENERAL,
        PERMISSIONS.VIEW_MACHINE_GENERAL,
        PERMISSIONS.VIEW_MATERIAL_GENERAL,
        PERMISSIONS.EDIT_MACHINE_PURCHASEPRICE,
        PERMISSIONS.EDIT_MATERIAL_PURCHASEPRICE,
      ],
      requiresFeatureSet: FEATURE_SETS.SUPPLIERS,
    },
  },
  {
    path: "/supplier/create",
    name: "SupplierCreate",
    component: () => import("@/views/supplier/SupplierCreate.vue"),
    meta: {
      title: "supplier.create.title",
      requiresAuth: true,
      requiresAllPermissions: [PERMISSIONS.EDIT_SUPPLIER_GENERAL],
      requiresFeatureSet: FEATURE_SETS.SUPPLIERS,
    },
  },
  {
    path: "/supplier/:supplierId",
    name: "SupplierDetail",
    props: true,
    component: () => import("@/views/supplier/SupplierDetail.vue"),
    meta: {
      title: "nav.supplier.overview",
      requiresAuth: true,
      requiresAllPermissions: [PERMISSIONS.VIEW_SUPPLIER_GENERAL],
      requiresFeatureSet: FEATURE_SETS.SUPPLIERS,
    },
  },
]
